import { CashRegisterPaymentMethod, Prisma } from "@prisma/client";
import { computablePricingValues } from "./pricing.types";
import { EventFormQuestion } from "./event-form-question.types";
import { Paginated, Sortable } from "./utils.types";

export const cashRegisterHolderValues = Prisma.validator<Prisma.CashRegisterHolderDefaultArgs>()({
  select: {
    id: true,
    isActive: true,
    sessions: {
      select: {
        cashRegister: {
          select: {
            id: true,
            name: true,
          },
        },
        closingStateId: true,
      },
    },
    eventCashRegisterId: true,
    user: {
      select: {
        lastName: true,
        firstName: true,
        connectId: true,
      },
    },
  },
});

export type CashRegisterHolder = Prisma.CashRegisterHolderGetPayload<
  typeof cashRegisterHolderValues
>;

export const cashRegisterValues = Prisma.validator<Prisma.CashRegisterDefaultArgs>()({
  include: {
    sessions: {
      select: {
        holder: {
          include: {
            user: {
              select: {
                connectId: true,
                firstName: true,
                lastName: true,
              },
            },
          },
        },
      },
    },
  },
});

export type CashRegister = Prisma.CashRegisterGetPayload<typeof cashRegisterValues>;

export type CashRegisterFilters = Sortable<
  Paginated<{
    searchText?: string;
  }>
>;

export const cashRegisterOrderValues = Prisma.validator<Prisma.OrderDefaultArgs>()({
  select: {
    id: true,
    reference: true,
    total: true,
    createdAt: true,
    cashRegisterPayment: {
      select: {
        paymentMethod: true,
      },
    },
    onlineCardPayment: {
      select: {
        transactionNumber: true,
      },
    },
    cashRegisterDetails: {
      select: {
        cancelledAt: true,
      },
    },
    orderItems: {
      select: {
        basePrice: true,
        finalTotal: true,
        ticket: {
          select: {
            id: true,
            code: true,
            hmac: true,
            ticketType: {
              select: {
                name: true,
                pricing: computablePricingValues,
                customAppearance: {
                  include: { picture: true },
                },
              },
            },
          },
        },
        badge: {
          select: {
            id: true,
            code: true,
            hmac: true,
            badgeType: {
              select: {
                name: true,
                pricing: computablePricingValues,
                customAppearance: {
                  include: { picture: true },
                },
              },
            },
          },
        },
      },
    },
  },
});

export type CashRegisterOrder = Prisma.OrderGetPayload<typeof cashRegisterOrderValues>;

export const cashRegisterSessionValues = Prisma.validator<Prisma.CashRegisterSessionDefaultArgs>()({
  select: {
    id: true,
    holder: {
      select: {
        id: true,
        user: {
          select: {
            firstName: true,
            lastName: true,
          },
        },
      },
    },
    cashRegister: {
      select: {
        id: true,
        name: true,
      },
    },
    closingState: true,
    openingState: true,
    changes: true,
    organiserLiquidGain: true,
    organiserCardGain: true,
    organiserCheckGain: true,
    organiserControlledAmount: true,
    orderDetails: {
      select: {
        id: true,
        cancelledAt: true,
        order: {
          select: {
            id: true,
            reference: true,
            total: true,
            createdAt: true,
            cashRegisterPayment: {
              select: {
                paymentMethod: true,
              },
            },
            onlineCardPayment: {
              select: {
                transactionNumber: true,
              },
            },
            orderItems: {
              select: {
                basePrice: true,
                finalTotal: true,
                buyableItem: {
                  select: {
                    badgeType: {
                      select: {
                        id: true,
                        name: true,
                        pricing: computablePricingValues,
                      },
                    },
                    ticketType: {
                      select: {
                        id: true,
                        name: true,
                        pricing: computablePricingValues,
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
      orderBy: {
        order: {
          createdAt: "asc",
        },
      },
    },
  },
});

export type CashRegisterSession = Prisma.CashRegisterSessionGetPayload<
  typeof cashRegisterSessionValues
>;

export type CashRegisterSessionListWithName = {
  sessions: CashRegisterSession[];
  name: string;
};

export type CashRegisterDailyDataUnit = {
  sessionId: string;
  cashRegisterId: string;
  cashRegisterName: string;
  cashRegisterHolderId: string;
  cashRegisterHolderName: string;
  soldEntriesCount: number;
  cashRegisterOpeningTotal: number;
  cashRegisterDeposits?: number;
  cashRegisterWithdrawals?: number;
  liquidGains: number;
  cashRegisterClosingTotal?: number;
  difference?: number;
};

export type CashRegisterDailyData = {
  sessions: CashRegisterDailyDataUnit[];
};

export type CashRegisterDailyHolderDataSells = {
  entryName: string;
  quantity: number;
  peopleCount: number;
  price: number;
  totalHT: number;
  TVA: number;
  totalTTC: number;
};

export type CashRegisterDailyHolderData = {
  difference: number;
  holderSummaryLiquid: number;
  holderSummaryCard: number;
  holderSummaryCheck: number;
  holderSummaryTotal: number;
  organiserSummaryLiquid: number;
  organiserSummaryCard: number;
  organiserSummaryCheck: number;
  organiserSummaryTotal: number;
  sells: CashRegisterDailyHolderDataSells[];
};

export type CashRegisterCommandSummary = {
  commandId: string;
  reference: string;
  entries: string;
  quantity: number;
  price: number;
  hour: Date;
  medium: CashRegisterPaymentMethod | null;
  cancelledAt: Date | null;
};

export type CashRegisterSessionGainSummary = {
  source: "holder" | "organiser";
  liquid: number;
  check: number;
  card: number;
};

export type CashRegisterDailyHolderDetailsData = {
  commands: CashRegisterCommandSummary[];
};

export type CashRegisterDailySummaryEntryHolderSells = {
  cashRegisterHolderId: string;
  count: number;
};

export type CashRegisterDailySummaryEntry = {
  name: string;
  sells: CashRegisterDailySummaryEntryHolderSells[];
  quantity: number;
  peopleCount: number;
  price: number;
  totalHT: number;
  TVA: number;
  totalTTC: number;
};

export type CashRegisterDailySummary = {
  entries: CashRegisterDailySummaryEntry[];
};

export type CashRegisterDailyDetailsSummary = {
  commands: CashRegisterCommandSummary[];
};

export type CashRegisterDailyStatisticsUnit = {
  cashRegisterHolder: string;
  count: number;
};

export type CashRegisterDailyStatistics = {
  units: CashRegisterDailyStatisticsUnit[];
};

export type CurrentCashRegisterType = { id: string; name: string };

export type SendReceiptEmailBody = {
  email: string;
};

export type SendReceiptEmailResponse = {
  success: boolean;
  reprintNumber: number;
};

export type ActivateCashRegisterResponse = {
  activated?: boolean;
  alreadyActivated?: boolean;
};

export type SetControlledAmountBody = {
  value: number | null;
};

export type SetOrganiserGainsBody = {
  liquid: number;
  card: number;
  check: number;
};

export type UpdatePaymentMethodBody = {
  paymentMethod: CashRegisterPaymentMethod;
};

// 400 code on the sale command when the answers do not match the current cash
// register form, so the POS can reopen the questions instead of showing a generic error
export const CASH_REGISTER_FORM_ANSWERS_INVALID_ERROR_CODE =
  "CASH_REGISTER_FORM_ANSWERS_INVALID";

export interface CashRegisterFormQuestions {
  eventId: string;
  questions: EventFormQuestion[];
}

// One answer per cash register form question, sent with the sale command
export interface CashRegisterCommandAnswer {
  questionId: string;
  answer?: string | null;
  answerList?: string[] | null;
}
